import { useCallback, useEffect, useRef, useState } from 'react';

interface UseLongPressOptions {
  /** Callback when long press is triggered */
  onLongPress: (e: React.TouchEvent | React.MouseEvent) => void;
  /** Duration in ms before long press triggers (default: 500ms) */
  threshold?: number;
  /** Optional callback on touch/mouse start */
  onPressStart?: (e: React.TouchEvent | React.MouseEvent) => void;
  /** Optional callback on touch/mouse end */
  onPressEnd?: (e: React.TouchEvent | React.MouseEvent) => void;
}

interface UseLongPressReturn {
  /** Whether currently pressing */
  pressing: boolean;
  /** Spread these handlers on the element */
  handlers: {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchEnd: (e: React.TouchEvent) => void;
    onTouchMove: () => void;
    onMouseDown: (e: React.MouseEvent) => void;
    onMouseUp: (e: React.MouseEvent) => void;
    onMouseLeave: () => void;
  };
}

/**
 * Hook for detecting long press gestures (mobile context menu)
 * - 500ms default threshold
 * - Cancels on finger/mouse movement
 * - Provides pressing state for visual feedback
 */
export function useLongPress({
  onLongPress,
  threshold = 500,
  onPressStart,
  onPressEnd
}: UseLongPressOptions): UseLongPressReturn {
  const [pressing, setPressing] = useState(false);
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Store latest callback to avoid stale closure in timeout
  const onLongPressRef = useRef(onLongPress);
  onLongPressRef.current = onLongPress;

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, []);

  const start = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      setPressing(true);
      onPressStart?.(e);
      // Store event data needed for callback (prevent stale reference)
      const eventData = {
        clientX: 'touches' in e ? e.touches[0]?.clientX : e.clientX,
        clientY: 'touches' in e ? e.touches[0]?.clientY : e.clientY,
        target: e.target
      };
      timeout.current = setTimeout(() => {
        // Create synthetic event with stored data
        const syntheticEvent = {
          preventDefault: () => {},
          clientX: eventData.clientX,
          clientY: eventData.clientY,
          target: eventData.target
        } as React.MouseEvent;
        onLongPressRef.current(syntheticEvent);
        setPressing(false);
      }, threshold);
    },
    [threshold, onPressStart]
  );

  const cancel = useCallback(
    (e?: React.TouchEvent | React.MouseEvent) => {
      setPressing(false);
      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = null;
      }
      if (e) onPressEnd?.(e);
    },
    [onPressEnd]
  );

  const handleTouchStart = useCallback((e: React.TouchEvent) => start(e), [start]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => cancel(e), [cancel]);

  const handleTouchMove = useCallback(() => cancel(undefined), [cancel]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => start(e), [start]);

  const handleMouseUp = useCallback((e: React.MouseEvent) => cancel(e), [cancel]);

  const handleMouseLeave = useCallback(() => cancel(undefined), [cancel]);

  return {
    pressing,
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchEnd,
      onTouchMove: handleTouchMove,
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseLeave
    }
  };
}
